import { makeArticle, notWorkingRelated } from "./learnFactory";

export const pageCanDepressionResistMedication = makeArticle({
  slug: "can-depression-become-resistant-to-medication",
  title: "Can Depression Become Resistant to Medication? | Neuro Wellness",
  description:
    "Depression can appear resistant to medication when adequate trials fail. Learn what treatment-resistant depression means, why it happens, and what options remain.",
  canonical: "/learn/can-depression-become-resistant-to-medication",
  h1: "Can Depression Become Resistant to Medication?",
  breadcrumbLabel: "Can Depression Resist Medication?",
  shortAnswer:
    "Yes. Depression can appear resistant to medication when symptoms do not improve adequately after one or more antidepressant trials at a therapeutic dose for a sufficient duration. This is commonly called treatment-resistant depression. It describes treatment history, not a separate diagnosis, and it does not mean the depression cannot be treated — other approaches, including TMS, SAINT®, and SPRAVATO®, may still help.",
  keyTakeaways: [
    "Depression can appear resistant to medication after adequate antidepressant trials fail.",
    "Treatment-resistant depression typically means inadequate response to at least two antidepressants.",
    "Resistance to one medication does not mean all treatments will fail.",
    "TMS, SAINT®, and SPRAVATO® work through different mechanisms than antidepressants.",
    "A psychiatric evaluation can clarify whether past trials were truly adequate.",
  ],
  sections: [
    {
      heading: "What Does It Mean for Depression to Resist Medication?",
      paragraphs: [
        "When depression does not improve adequately after an antidepressant has been taken at a therapeutic dose for a sufficient duration — typically 6 to 8 weeks — clinicians may describe the depression as not responding to that medication. If this happens with two or more antidepressants, it may meet common definitions of treatment-resistant depression.",
        "Treatment-resistant depression is not a separate diagnosis. It describes a treatment history: the medications tried so far have not produced adequate relief. It does not mean the depression is permanent or that no other treatments exist.",
      ],
    },
    {
      heading: "Why Depression May Not Respond to Medication",
      paragraphs: [
        "Several factors can contribute to an apparent lack of response to antidepressants:",
      ],
      list: [
        "The dose was too low or the trial too short to be considered adequate.",
        "The medication was stopped due to side effects before it could take effect.",
        "An undiagnosed condition, such as bipolar disorder, is present and requires different treatment.",
        "Comorbid conditions like anxiety, PTSD, or substance use are interfering with recovery.",
        "Medical conditions such as hypothyroidism or sleep apnea are contributing to symptoms.",
        "The specific medication class was not the right fit for that individual's neurobiology.",
      ],
    },
    {
      heading: "What This Means for Patients",
      paragraphs: [
        "If your depression has not responded to medication, the most important next step is a thorough psychiatric re-evaluation. A psychiatrist can review whether past trials were truly adequate, screen for conditions that may have been missed, and discuss treatments that work through different mechanisms than antidepressants.",
        "FDA-cleared TMS and SAINT® accelerated TMS are specifically indicated for adults whose depression has not improved on antidepressant medication. SPRAVATO® (esketamine) is FDA-approved for treatment-resistant depression. These treatments do not rely on the same chemical pathways as standard antidepressants.",
        "Do not stop your current medication on your own. Talk to your prescribing clinician before making any changes.",
      ],
      links: [
        {
          to: "/depression-treatment-not-working",
          text: "See our full guide on when treatment isn't working",
        },
        {
          to: "/contactus",
          text: "Request a consultation",
          suffix: " or call 954-827-9793.",
        },
      ],
    },
  ],
  faqs: [
    {
      question: "Can depression become resistant to medication?",
      answer:
        "Yes. Depression can appear resistant to medication when symptoms do not improve adequately after one or more antidepressant trials at a therapeutic dose for a sufficient duration. This is commonly called treatment-resistant depression. It describes treatment history, not a separate diagnosis, and other treatments may still help.",
    },
    {
      question: "Does resistant mean my depression can't be treated?",
      answer:
        "No. Treatment-resistant depression means the medications tried so far have not worked — not that no treatment will work. TMS, SAINT®, SPRAVATO®, and ECT are evidence-based options that work through different mechanisms than antidepressants and are specifically used when medication has not produced adequate relief.",
    },
    {
      question:
        "How many antidepressants need to fail before it's treatment-resistant?",
      answer:
        "Common definitions require inadequate response to at least two antidepressant trials of adequate dose and duration in the current episode. However, some clinicians consider other factors, such as whether different medication classes were tried and whether psychotherapy was included. A psychiatrist makes this determination based on your full history.",
    },
    {
      question: "What should I do if my antidepressant isn't working?",
      answer:
        "Do not stop your medication on your own. Talk to your prescribing clinician. They can review whether the dose and duration were adequate, check for contributing factors, and discuss options including medication adjustments, psychotherapy, or non-medication treatments like TMS or SAINT®.",
    },
    {
      question: "If you are having thoughts of self-harm, what should you do?",
      answer:
        "Call or text 988 (Suicide & Crisis Lifeline) now, or go to your nearest emergency room. You do not have to be in active crisis to call — trained counselors are available 24/7.",
    },
  ],
  sources: [
    {
      citation:
        "Rush AJ, et al. Acute and longer-term outcomes in depressed outpatients requiring one or several treatment steps: a STAR*D report. Am J Psychiatry. 2006;163(11):1905-1917.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17074942/",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) K220177 — Magnus Neuromodulation System with SAINT Technology (September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],
  relatedLinks: notWorkingRelated,
});
